export type ProviderType = 'selfhosted' | 'gemini' | 'ollama' | 'lmstudio' | 'groq' | 'openrouter' | 'onnx';

export interface ProviderConfig {
  type: ProviderType;
  apiKey?: string;
  baseUrl?: string;
  model: string;
  enabled: boolean;
}

export interface ProviderModel {
  id: string;
  name: string;
  contextLength?: number;
  description?: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export interface GenerateRequest {
  messages: ChatMessage[];
  systemPrompt?: string;
  temperature?: number;
  maxTokens?: number;
  stream?: boolean;
}

export interface GenerateResponse {
  text: string;
  model: string;
  finishReason?: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

export interface SearchResult {
  title: string;
  url: string;
  snippet: string;
}


export interface StreamUsage {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
}

export type StreamEvent =
  | { type: 'text'; content: string }
  | { type: 'usage'; usage: StreamUsage }
  | { type: 'error'; error: string };

export interface AIProvider {
  readonly type: ProviderType;
  readonly name: string;
  readonly supportsStreaming: boolean;
  readonly supportsFunctionCalling: boolean;
  readonly requiresApiKey: boolean;

  configure(config: ProviderConfig): void;

  isConfigured(): boolean;

  testConnection(): Promise<{ success: boolean; message: string; models?: ProviderModel[] }>;

  listModels(): Promise<ProviderModel[]>;

  generate(request: GenerateRequest): Promise<GenerateResponse>;

  generateStream?(request: GenerateRequest): AsyncGenerator<StreamEvent, void, unknown>;
}

export interface ProviderCapabilities {
  functionCalling: boolean;
  googleSearchGrounding: boolean;
  streaming: boolean;
  visionSupport: boolean;
  maxContextLength: number;
}

export const PROVIDER_DEFAULTS: Record<ProviderType, Partial<ProviderConfig> & { defaultModels: string[] }> = {
  selfhosted: {
    baseUrl: 'http://localhost:8800',
    model: 'apriel-1.6-15b-thinker',
    defaultModels: [
      'apriel-1.6-15b-thinker',
      'deepseek-r1-qwen3-8b',
      'deepseek-r1-distill-3b',
      'gpt-oss-20b'
    ]
  },
  gemini: {
    baseUrl: 'https://generativelanguage.googleapis.com',
    model: 'gemini-2.5-pro',
    defaultModels: ['gemini-2.5-pro', 'gemini-2.5-flash', 'gemini-2.0-flash']
  },
  ollama: {
    baseUrl: 'http://localhost:11434',
    model: 'llama3.2:1b',
    defaultModels: [
      'smollm:135m',
      'gemma3:1b',
      'llama3.2:1b',
      'qwen2.5:1.5b',
      'gemma2:2b',
      'llama3.2:3b',
      'qwen2.5:3b',
      'phi3.5:3.8b',
      'mistral:7b',
      'llama3.1:8b',
      'qwen2.5:7b',
      'gemma2:9b',
      'deepseek-coder:6.7b',
      'qwen2.5:14b'
    ]
  },
  lmstudio: {
    baseUrl: 'http://localhost:1234',
    model: 'local-model',
    defaultModels: []
  },
  groq: {
    baseUrl: 'https://api.groq.com/openai/v1',
    model: 'llama-3.3-70b-versatile',
    defaultModels: ['llama-3.3-70b-versatile', 'llama-3.1-8b-instant', 'mixtral-8x7b-32768', 'gemma2-9b-it']
  },
  openrouter: {
    baseUrl: 'https://openrouter.ai/api/v1',
    model: 'anthropic/claude-3.5-sonnet',
    defaultModels: ['anthropic/claude-3.5-sonnet', 'openai/gpt-4o', 'google/gemini-pro-1.5', 'meta-llama/llama-3.1-70b-instruct']
  },
  onnx: {
    model: 'LiquidAI/LFM2.5-1.2B-Thinking-ONNX',
    defaultModels: ['LiquidAI/LFM2.5-1.2B-Thinking-ONNX']
  }
};

export const PROVIDER_INFO: Record<ProviderType, { name: string; description: string; costType: 'free' | 'free-tier' | 'paid' | 'local' }> = {
  selfhosted: {
    name: 'Self-Hosted Server',
    description: 'Your own AI gateway with Ollama, SearXNG search, and Piper TTS. Zero API costs, full privacy, GPT-4o level performance.',
    costType: 'free'
  },
  gemini: {
    name: 'Google Gemini',
    description: 'Premium cloud AI with Google Search grounding and function calling',
    costType: 'free-tier'
  },
  ollama: {
    name: 'Ollama (Local)',
    description: 'Run models locally - free, private, works on desktop and Android via Termux',
    costType: 'local'
  },
  lmstudio: {
    name: 'LM Studio (Local)',
    description: 'OpenAI-compatible local server with easy GUI model management',
    costType: 'local'
  },
  groq: {
    name: 'Groq',
    description: 'Ultra-fast cloud inference with generous free tier',
    costType: 'free-tier'
  },
  openrouter: {
    name: 'OpenRouter',
    description: 'Access many models (Claude, GPT-4, etc.) through one API',
    costType: 'paid'
  },
  onnx: {
    name: 'AI Edge (WebGPU)',
    description: 'Run Liquid LFM 2.5 directly in your browser. 100% Private. No install required.',
    costType: 'free'
  }
};
